import React, { useState } from 'react';

export interface MealOption { id: string; meal_type?: string; description?: string; carbs?: number | null }

interface BolusEntryFormProps {
  onSave: (entryData: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  /** The day's meals, so the bolus can be linked to one (BO-3 / BO-4). */
  meals?: MealOption[];
}

const NEW_MEAL = '__new__';
export const mealLabel = (m: MealOption) =>
  `${(m.meal_type || 'meal').replace(/^./, (c) => c.toUpperCase())}${m.description ? ` · ${m.description}` : ''}${m.carbs ? ` · ${m.carbs}g` : ''}`;

/**
 * Meal link rules (REQUIREMENTS_NOTEBOOK BO-3, BO-5; BOLUS_NOTES-74/75):
 * - an edit sends `mealId` only when the link was changed (null = unlink), so
 *   saving a bolus never drops a link by accident;
 * - "Add meal" on a NEW bolus sends a nested `meal` (type, the food text, carbs)
 *   and the server saves both, linked, in one call.
 */
const BolusEntryForm: React.FC<BolusEntryFormProps> = ({ onSave, onCancel, initialData, meals = [] }) => {
  const [insulinUnits, setInsulinUnits] = useState<string>(initialData?.insulin_units?.toString() || '');
  const [carbsConsumed, setCarbsConsumed] = useState<string>(initialData?.carbs_consumed?.toString() || '');
  const [glucoseBefore, setGlucoseBefore] = useState<string>(initialData?.blood_glucose_before?.toString() || '');
  const [mealType, setMealType] = useState<string>(initialData?.meal_type || 'bolus');
  const [injectionSite, setInjectionSite] = useState<string>(initialData?.injection_site || '');
  const [foodDescription, setFoodDescription] = useState<string>(initialData?.food_description || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialMeal: string = initialData?.meal_id || '';
  const [mealChoice, setMealChoice] = useState<string>(initialMeal);
  const linkedGone = !!initialMeal && !meals.some((m) => m.id === initialMeal);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const parsedInsulin = parseFloat(insulinUnits) || 0;
      const parsedCarbs = parseFloat(carbsConsumed) || 0;
      const parsedGlucose = parseFloat(glucoseBefore) || 0;
      const safeNotes = (foodDescription || '').trim();
      const safeSite = (injectionSite || '').trim();

      if (parsedInsulin === 0) {
        const hasMeaningfulData = parsedCarbs > 0 || parsedGlucose > 0 || safeNotes.length > 0 || safeSite.length > 0 || !!mealChoice;
          
        if (!hasMeaningfulData) {
          alert("Cannot save an entry with 0 insulin and no other data (carbs, glucose, notes).");
          setIsSubmitting(false);
          return;
        }
        
        const confirmZero = window.confirm("You are about to save an entry with 0.0 units of insulin. Are you sure you want to proceed?");
        if (!confirmZero) {
          setIsSubmitting(false);
          return;
        }
      }

      const data: Record<string, any> = {
        insulin_units: parsedInsulin,
        carbs_consumed: parsedCarbs > 0 ? parsedCarbs : null,
        blood_glucose_before: parsedGlucose > 0 ? parsedGlucose : null,
        meal_type: mealType,
        injection_site: injectionSite || null,
        food_description: foodDescription || null,
      };
      if (mealChoice === NEW_MEAL) {
        // BO-5: the food text becomes the meal; the bolus keeps the carbs it was dosed for.
        data.meal = { mealType: mealType === 'bolus' ? 'meal' : mealType, description: safeNotes, carbs: parsedCarbs > 0 ? parsedCarbs : null };
        data.food_description = null;
      } else if (mealChoice !== initialMeal) {
        data.mealId = mealChoice || null;   // BO-3: only a changed link is sent
      }
      await onSave(data);
    } catch (err) {
      console.error('Failed to save bolus entry', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="entry-form popup-form">
      <h3>{initialData ? 'Edit Bolus Entry' : 'New Bolus Entry'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Insulin Units</label>
          <input 
            type="number" 
            step="0.1" 
            value={insulinUnits} 
            onChange={e => setInsulinUnits(e.target.value)} 
            required 
            placeholder="0.0"
          />
        </div>
        <div className="form-group">
          <label>Carbs Consumed (g)</label>
          <input 
            type="number" 
            step="1" 
            value={carbsConsumed} 
            onChange={e => setCarbsConsumed(e.target.value)} 
            placeholder="e.g. 45"
          />
        </div>
        <div className="form-group">
          <label>Blood Glucose Before (mg/dL)</label>
          <input 
            type="number" 
            step="1" 
            value={glucoseBefore} 
            onChange={e => setGlucoseBefore(e.target.value)} 
            placeholder="e.g. 120"
          />
        </div>
        <div className="form-group">
          <label>Meal Type</label>
          <select value={mealType} onChange={e => setMealType(e.target.value)}>
            <option value="bolus">Correction Bolus</option>
            <option value="breakfast">Breakfast</option>
            <option value="lunch">Lunch</option>
            <option value="dinner">Dinner</option>
            <option value="snack">Snack</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="bolus-meal">Meal</label>
          <select id="bolus-meal" value={mealChoice} onChange={e => setMealChoice(e.target.value)}>
            <option value="">No meal</option>
            {linkedGone && <option value={initialMeal}>Linked meal (another day)</option>}
            {meals.map((m) => <option key={m.id} value={m.id}>{mealLabel(m)}</option>)}
            {!initialData && <option value={NEW_MEAL}>+ Add meal from this entry</option>}
          </select>
          {mealChoice === NEW_MEAL && <small className="nb-hint">Saves a meal with this type, food and carbs, linked to the bolus.</small>}
          {initialMeal && !mealChoice && <small className="nb-hint">Saving will unlink the meal (the meal itself stays).</small>}
        </div>
        <div className="form-group">
          <label>Injection Site</label>
          <input 
            type="text" 
            value={injectionSite} 
            onChange={e => setInjectionSite(e.target.value)} 
            placeholder="e.g. Left Thigh"
          />
        </div>
        <div className="form-group">
          <label>Food Description</label>
          <textarea 
            value={foodDescription} 
            onChange={e => setFoodDescription(e.target.value)} 
            placeholder="What did you eat?"
            rows={3}
          />
        </div>
        <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BolusEntryForm;
